/**
 * O'ng pastki burchakdagi AI yordamchi: puls halqali dumaloq tugma va uni
 * bosganda ochiladigan chat oynasi.
 *
 * Javoblar hozircha lokal — `shared/data/content.ts` dagi kalit so'z jadvali
 * bo'yicha tanlanadi, tashqi modelga so'rov yuborilmaydi. Backend ulangach
 * `ask()` ichidagi timeout o'rniga `POST /api/v1/assistant/ask/` chaqiriladi,
 * qolgan mantiq (navbat, «yozmoqda» holati, fokus boshqaruvi) o'zgarmaydi.
 *
 * `prefers-reduced-motion` yoqilgan bo'lsa javob kechikishi ham, silliq
 * scroll ham ishlatilmaydi.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { assistantApi } from "@/shared/api/assistant";
import type { ChatMessage, PageContext } from "@/shared/api/assistant";
import { assistant } from "@/shared/data/content";
import { useI18n } from "@/shared/i18n";
import { prefersReducedMotion } from "@/shared/lib/motion";
import { perform, snapshot } from "@/shared/lib/pageAgent";
import { Icon } from "@/shared/ui/Icon";

interface Message {
  id: number;
  role: "bot" | "user" | "step";
  text: string;
}

/** Kabinet ichidagi rejim: haqiqiy LLM, tizim ma'lumotlari va sahifani boshqarish. */
export interface AgentConfig {
  pages: Array<{ path: string; label: string }>;
  greeting: string;
  suggestions: string[];
  /** Kirmagan mehmon (landing): ochiq ma'lumot asboblari, alohida endpoint. */
  public?: boolean;
}

/** Bitta foydalanuvchi so'rovi uchun maksimal harakat navbatlari (halqadan saqlanish). */
const MAX_TURNS = 8;

/** O'zbek matnida uchraydigan turli apostroflar bitta belgiga keltiriladi. */
const APOSTROPHES = /[ʻʼ‘’`´]/g;

function normalize(text: string): string {
  return text.toLowerCase().replace(APOSTROPHES, "'");
}

function answerFor(question: string): string {
  const asked = normalize(question);
  const hit = assistant.replies.find((reply) =>
    reply.keys.some((key) => asked.includes(normalize(key))),
  );
  return hit ? hit.text : assistant.fallback;
}

export function AiAssistant({ agent }: { agent?: AgentConfig }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: 0, role: "bot", text: agent?.greeting ?? assistant.greeting },
  ]);
  const history = useRef<ChatMessage[]>([]);
  const abort = useRef<AbortController | null>(null);
  const agentRef = useRef(agent);
  agentRef.current = agent;

  const fabRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const nextId = useRef(1);
  const timers = useRef<number[]>([]);

  /* Sahifadan chiqishda navbatdagi javob taymerlari bekor qilinadi. */
  useEffect(() => {
    const list = timers.current;
    return () => list.forEach((id) => window.clearTimeout(id));
  }, []);

  /* Bir marta ko'rsatiladigan eslatma pufakchasi — oyna ochilgach yo'qoladi. */
  useEffect(() => {
    if (everOpened) return;
    const id = window.setTimeout(() => setTeaser(true), 4500);
    return () => window.clearTimeout(id);
  }, [everOpened]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        fabRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /* Yangi xabar qo'shilganda lenta oxiriga suriladi. */
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    log.scrollTo({
      top: log.scrollHeight,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [messages, typing]);

  const push = useCallback((role: Message["role"], text: string) => {
    if (text) setMessages((list) => [...list, { id: nextId.current++, role, text }]);
  }, []);

  /** Agent halqasi: model → (ma'lumot asbobi serverda) → interfeys harakati → yangi sahifa holati → model. */
  const runAgent = useCallback(
    async (text: string) => {
      const config = agentRef.current;
      if (!config) return;
      const controller = new AbortController();
      abort.current = controller;
      history.current = [...history.current, { role: "user", content: text } as ChatMessage].slice(-30);
      setTyping(true);
      try {
        for (let turn = 0; turn < MAX_TURNS; turn++) {
          const page: PageContext = { ...snapshot(), pages: config.pages };
          const result = await assistantApi.chat(history.current, page, controller.signal, config.public);
          history.current = [...history.current, ...result.append];
          if (result.type === "reply" || !result.actions?.length) {
            push("bot", result.reply || "Tayyor.");
            return;
          }
          push("bot", result.reply);
          for (const action of result.actions) {
            const outcome = await perform(action.name, action.args, {
              navigate,
              allowedPaths: config.pages.map((item) => item.path),
              confirm: (question) => Promise.resolve(window.confirm(question)),
            });
            push("step", outcome.trace);
            history.current = [
              ...history.current,
              { role: "tool", tool_call_id: action.id, content: outcome.message },
            ];
            if (!outcome.ok) break;
          }
        }
        push("bot", "Harakatlar chegarasiga yetdim. Kerak bo'lsa davom ettirishni ayting.");
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          push("bot", error instanceof Error ? error.message : "Xatolik yuz berdi.");
        }
      } finally {
        abort.current = null;
        setTyping(false);
      }
    },
    [navigate, push],
  );

  const stop = () => abort.current?.abort();

  const ask = useCallback((raw: string) => {
    const text = raw.trim();
    if (!text) return;

    setMessages((list) => [...list, { id: nextId.current++, role: "user", text }]);
    setDraft("");
    if (agentRef.current) {
      void runAgent(text);
      return;
    }
    setTyping(true);

    // "O'ylash" pauzasi javob uzunligiga bog'liq — jonli suhbat taassuroti.
    const delay = prefersReducedMotion() ? 0 : 520 + Math.min(900, text.length * 18);
    const timer = window.setTimeout(() => {
      setTyping(false);
      setMessages((list) => [
        ...list,
        { id: nextId.current++, role: "bot", text: answerFor(text) },
      ]);
    }, delay);
    timers.current.push(timer);
  }, [runAgent]);

  const toggle = () => {
    setTeaser(false);
    setEverOpened(true);
    setOpen((value) => !value);
  };

  return (
    <div className="ai-dock" data-open={open ? "" : undefined}>
      {/* Yopiq holatda oyna `visibility: hidden` bo'ladi — ichidagi maydonlar
          Tab bilan topilmaydi, shuning uchun `aria-hidden` xavfsiz. */}
      <div
        className="ai-panel"
        id="ai-assistant-panel"
        role="dialog"
        aria-label={assistant.title}
        aria-hidden={open ? undefined : "true"}
      >
        <header className="ai-head">
          <span className="ai-avatar" aria-hidden="true">
            <Icon name="sparkles" size={20} />
          </span>
          <span className="ai-head-text">
            <b>{assistant.title}</b>
            <span>
              <i className="ai-online" aria-hidden="true" />
              {assistant.status}
            </span>
          </span>
          <button
            type="button"
            className="ai-close"
            onClick={() => {
              setOpen(false);
              fabRef.current?.focus();
            }}
            aria-label={t("ai.close")}
          >
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="ai-log" ref={logRef} role="log" aria-live="polite">
          {messages.map((message) => (
            <p className={`ai-msg ai-msg-${message.role}`} key={message.id}>
              {message.text}
            </p>
          ))}

          {typing ? (
            <p className="ai-msg ai-msg-bot ai-typing" aria-label={t("ai.typing")}>
              <i aria-hidden="true" />
              <i aria-hidden="true" />
              <i aria-hidden="true" />
            </p>
          ) : null}

          {typing && agent ? (
            <button type="button" className="ai-stop" onClick={stop}>
              To'xtatish
            </button>
          ) : null}

          {messages.length === 1 && !typing ? (
            <div className="ai-chips">
              {(agent?.suggestions ?? assistant.suggestions).map((item) => (
                <button type="button" key={item} onClick={() => ask(item)}>
                  {item}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <form
          className="ai-form"
          onSubmit={(event) => {
            event.preventDefault();
            ask(draft);
          }}
        >
          <label className="visually-hidden" htmlFor="ai-input">
            {assistant.placeholder}
          </label>
          <input
            id="ai-input"
            ref={inputRef}
            className="ai-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={assistant.placeholder}
            autoComplete="off"
          />
          <button
            type="submit"
            className="ai-send"
            disabled={!draft.trim()}
            aria-label={t("action.send")}
          >
            <Icon name="send" size={18} />
          </button>
        </form>

        <p className="ai-foot">{assistant.disclaimer}</p>
      </div>

      {teaser && !open ? (
        <p className="ai-teaser">Savolingiz bormi? AI yordamchi javob beradi.</p>
      ) : null}

      <button
        type="button"
        ref={fabRef}
        className="ai-fab"
        onClick={toggle}
        aria-expanded={open}
        aria-controls="ai-assistant-panel"
        aria-label={open ? t("ai.close") : t("ai.open")}
      >
        <span className="ai-fab-ring" aria-hidden="true" />
        <span className="ai-fab-ring" aria-hidden="true" />
        <Icon name={open ? "close" : "sparkles"} size={24} />
      </button>
    </div>
  );
}
